// 계약 사실화 1단계 정정8(26-07-29) — **금액 0 은 '없음'이다.** 그 판정의 정본.
//
// 규칙 한 줄: 금액 칸의 값이 비었거나 0 이면 **아무 금액도 보여 주지 않는다**
// (표기는 '없음' — 화면 관례에 따라 en-dash '–' 또는 MaskedPrice 의 빈 값 표기).
//
// 왜 이 규칙인가 — 이 시스템의 금액 컬럼은 NOT NULL 이 많아 **'비움'이 0 으로 저장된다.**
// 그 0 을 금액으로 찍으면 화면이 거짓을 말한다(금액이 없는 계약을 '0원 계약'이라고
// 말한다). 같은 판정의 선례가 이 리포에 이미 세 건 있었다(정정7 이 찾아 적었다):
//   ① 고객사 마스터 편집 모달 — "0 은 빈 칸으로 보여 준다"(0 으로 되읽으면 지운 칸에
//      0 이 되살아난 것처럼 보이므로).
//   ② 계약서 PDF 의 가격 카드 — bigint `0n` 이 falsy 라 금액 0 을 아예 인쇄하지 않는다.
//   ③ 사업 대시보드 — 상용 줄의 금액 0 을 '미입력'으로 세어 '금액 확인 필요' 경고를 켠다.
//
// 이 파일이 생긴 경위(정정8) — 정정7 이 이 판정을 계약 상세 화면 안에 지역 함수
// (`amountOrNone`)로 두었더니, **같은 값을 읽는 다른 화면들이 각자 다른 말을 했다**:
//   • 계약 상세      — 판매 계약의 월 구독료: 표기 없음('-')
//   • 고객사 상세    — 같은 계약의 '구독료(대당)'·'구독료 소계'·'총 구독료': **₩0**
//   • 고객사 목록    — 같은 계약의 '총 구독료': **₩0**
// (판매 계약의 금액은 판매가 칸에 있고 월 구독료 칸은 NOT NULL 이라 0 으로 저장된다.
//  그래서 세 화면이 같은 계약을 두고 "월 구독료 없음" / "구독료 ₩0" 로 갈렸다.)
// 판정을 화면마다 두면 반드시 다시 갈라지므로 **규칙을 여기 한 벌만 둔다** —
// 표기 정본을 lib 에 모으는 기존 관례와 같다(lib/contract-reason, lib/format-date).
//
// ⚠ 가격 권한(마스킹)보다 **뒤에서** 판정하라. 0 을 접는 것은 값의 표기이고, 마스킹은
// 역할에 따라 값 자체를 가리는 것이다. 순서가 뒤집히면 가격 권한이 없는 사용자에게
// "0 이냐 아니냐"가 새로 새어 나간다. 부르는 자리는 모두 다음 모양이어야 한다:
//   priceVisible ? formatKrwOrNone(값) : '비공개'      (고객사 화면)
//   <MaskedPrice value={amountOrNone(값)} />           (계약 상세 — 컴포넌트가 뒤에서 가린다)
//
// ⚠ 여기서 접는 것은 **금액**뿐이다. 대수·건수 같은 개수는 0 이 사실이므로 접지 않는다.

/**
 * 금액 없음 표기 — en-dash. 고객사 상세·고객사 목록이 종전부터 쓰던 글자이고,
 * lib/contract-reason 의 CONTRACT_REASON_EMPTY 와 같은 글자다(빈 값 표기 관례).
 */
export const AMOUNT_NONE = '–';

/**
 * 금액 값을 정본 판정으로 정규화한다. **0 은 '없음'이므로 null 로 접는다.**
 *
 * 받는 형태가 두 갈래인 이유 — 서버가 계약 상세에는 BigInt 를 문자열로 내리고
 * (전역 직렬화), 고객사 화면에는 number 로 내린다(customer-master.row 가 변환).
 * 같은 판정을 두 벌로 만들지 않기 위해 둘 다 받는다.
 *
 * @returns 보여 줄 금액 문자열, 또는 **없음이면 null**.
 *   숫자가 아닌 값(서버가 형식을 바꾼 경우)은 접지 않고 원문을 돌려준다 —
 *   조용히 금액을 없애 버리지 않는다(호출부가 그대로 보여 준다).
 */
export function amountOrNone(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null;
  const s = String(value);
  return Number(s) === 0 ? null : s;
}

/**
 * 금액을 원화 표기로. **없음(빈 값·0)이면 금액 대신 '없음' 표기를 돌려준다.**
 *
 * 고객사 상세/목록이 각자 들고 있던 `fmtKrw` / `formatKRW`(0 을 '₩0' 으로 찍던
 * 두 벌)를 이 함수로 모았다. 계약 상세는 MaskedPrice 가 빈 값 표기를 담당하므로
 * `amountOrNone` 을 직접 부른다 — 판정은 어느 쪽이든 같은 한 벌이다.
 *
 * @param emptyText 없음일 때 쓸 글자(기본 en-dash). 화면 관례가 다르면 넘긴다.
 */
export function formatKrwOrNone(
  value: string | number | null | undefined,
  emptyText: string = AMOUNT_NONE,
): string {
  const amount = amountOrNone(value);
  if (amount === null) return emptyText;
  const n = Number(amount);
  // 숫자가 아니면 천 단위 구분을 붙이지 않고 원문 그대로(위 amountOrNone 주석).
  return Number.isFinite(n) ? `₩${n.toLocaleString('ko-KR')}` : amount;
}
